import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MarineOrganizationAccess } from './AuthGate';
import { supabase } from './lib/supabase';
import './showings-calendar.css';

type Row = any;
type CalendarEvent = Row & {
  display_title:string;
  vessel_label:string|null;
  buyer_label:string|null;
  broker_label:string|null;
  display_location:string|null;
};

type Props={organization:MarineOrganizationAccess};

const pad=(value:number)=>String(value).padStart(2,'0');
const human=(value:any)=>String(value||'').replaceAll('_',' ').replace(/\b\w/g,(m)=>m.toUpperCase());
const localDateKey=(value:Date)=>`${value.getFullYear()}-${pad(value.getMonth()+1)}-${pad(value.getDate())}`;
const toLocalInput=(value?:string|null)=>{
  const d=value?new Date(value):new Date();
  if(Number.isNaN(d.getTime()))return '';
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const timeLabel=(value:any)=>value?new Date(value).toLocaleTimeString('en-CA',{hour:'numeric',minute:'2-digit'}):'';
const fullDate=(value:any)=>value?new Date(value).toLocaleString('en-CA',{weekday:'short',year:'numeric',month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}):'—';
const monthLabel=(value:Date)=>value.toLocaleDateString('en-CA',{month:'long',year:'numeric'});
const isSameMonth=(a:Date,b:Date)=>a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth();

function monthCells(month:Date){
  const first=new Date(month.getFullYear(),month.getMonth(),1);
  const start=new Date(first);
  start.setDate(first.getDate()-first.getDay());
  return Array.from({length:42},(_,index)=>{
    const d=new Date(start);
    d.setDate(start.getDate()+index);
    return d;
  });
}

function addMinutes(value:Date,minutes:number){return new Date(value.getTime()+minutes*60_000)}

export default function ShowingsCalendarBridge({organization}:Props){
  const [navHost,setNavHost]=useState<HTMLElement|null>(null);
  const [pageHost,setPageHost]=useState<HTMLElement|null>(null);
  const [open,setOpen]=useState(false);
  const [month,setMonth]=useState(()=>new Date(new Date().getFullYear(),new Date().getMonth(),1));
  const [showings,setShowings]=useState<Row[]>([]);
  const [listings,setListings]=useState<Row[]>([]);
  const [vessels,setVessels]=useState<Row[]>([]);
  const [inquiries,setInquiries]=useState<Row[]>([]);
  const [members,setMembers]=useState<Row[]>([]);
  const [profiles,setProfiles]=useState<Row[]>([]);
  const [currentUserId,setCurrentUserId]=useState('');
  const [canEdit,setCanEdit]=useState(organization.roles.includes('platform_admin'));
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [statusFilter,setStatusFilter]=useState('all');
  const [brokerFilter,setBrokerFilter]=useState('all');
  const [editorOpen,setEditorOpen]=useState(false);
  const [editing,setEditing]=useState<Row|null>(null);
  const [seedDate,setSeedDate]=useState('');
  const [formError,setFormError]=useState('');
  const [saving,setSaving]=useState(false);
  const loadedOnce=useRef(false);

  useEffect(()=>{
    const nav=document.querySelector('.sidebar nav') as HTMLElement|null;
    const main=document.querySelector('.main-shell') as HTMLElement|null;
    if(!nav||!main)return;

    let navMount=document.getElementById('showings-calendar-nav-host') as HTMLElement|null;
    if(!navMount){
      navMount=document.createElement('div');
      navMount.id='showings-calendar-nav-host';
      navMount.className='showings-calendar-nav-host';
      const mina=[...nav.querySelectorAll('button')].find((button)=>button.textContent?.includes('Mina'));
      nav.insertBefore(navMount,mina||null);
    }
    let pageMount=document.getElementById('showings-calendar-page-host') as HTMLElement|null;
    if(!pageMount){
      pageMount=document.createElement('div');
      pageMount.id='showings-calendar-page-host';
      main.appendChild(pageMount);
    }
    setNavHost(navMount);setPageHost(pageMount);

    const handleNav=(event:Event)=>{
      const target=event.target as HTMLElement;
      const button=target.closest('.sidebar .nav-button');
      if(button&&!button.classList.contains('showings-calendar-nav-button'))setOpen(false);
    };
    document.addEventListener('click',handleNav,true);
    return()=>document.removeEventListener('click',handleNav,true);
  },[]);

  useEffect(()=>{
    document.body.classList.toggle('marine-showings-calendar-open',open);
    if(open&&!loadedOnce.current){loadedOnce.current=true;void load()}
    return()=>document.body.classList.remove('marine-showings-calendar-open');
  },[open]);

  useEffect(()=>{loadedOnce.current=false;setShowings([]);if(open)void load()},[organization.id]);

  async function load(){
    if(!supabase)return;
    setLoading(true);setError('');
    const user=await supabase.auth.getUser();
    const uid=user.data.user?.id||'';setCurrentUserId(uid);
    const [listingResult,vesselResult,showingResult,inquiryResult,memberResult]=await Promise.all([
      supabase.from('marine_listings').select('id,vessel_id,listing_code,stage,listing_broker_user_id').eq('organization_id',organization.id).order('created_at',{ascending:false}),
      supabase.from('marine_vessels').select('id,year,make,model,vessel_name,location').eq('organization_id',organization.id),
      supabase.from('marine_listing_showings').select('*').eq('organization_id',organization.id).order('scheduled_at',{ascending:true}),
      supabase.from('marine_listing_inquiries').select('id,listing_id,lead_name,phone,email').eq('organization_id',organization.id),
      supabase.from('marine_organization_members').select('user_id,role,active,can_draft').eq('organization_id',organization.id),
    ]);
    const firstError=listingResult.error||vesselResult.error||showingResult.error||inquiryResult.error||memberResult.error;
    if(firstError){setError(firstError.message);setLoading(false);return}
    setListings(listingResult.data||[]);setVessels(vesselResult.data||[]);setShowings(showingResult.data||[]);setInquiries(inquiryResult.data||[]);setMembers(memberResult.data||[]);
    const memberIds=(memberResult.data||[]).map((row:Row)=>row.user_id).filter(Boolean);
    if(memberIds.length){
      const profileResult=await supabase.from('marine_profiles').select('user_id,full_name,email').in('user_id',memberIds);
      if(!profileResult.error)setProfiles(profileResult.data||[]);
    }else setProfiles([]);
    const own=(memberResult.data||[]).find((row:Row)=>row.user_id===uid);
    setCanEdit(organization.roles.includes('platform_admin')||Boolean(own?.active&&(own?.can_draft||['owner_admin','broker','broker_assistant'].includes(own?.role))));
    setLoading(false);
  }

  const vesselById=useMemo(()=>Object.fromEntries(vessels.map((row:Row)=>[row.id,row])),[vessels]);
  const listingById=useMemo(()=>Object.fromEntries(listings.map((row:Row)=>[row.id,row])),[listings]);
  const inquiryById=useMemo(()=>Object.fromEntries(inquiries.map((row:Row)=>[row.id,row])),[inquiries]);
  const profileById=useMemo(()=>Object.fromEntries(profiles.map((row:Row)=>[row.user_id,row])),[profiles]);

  const enriched=useMemo<CalendarEvent[]>(()=>showings.map((row:Row)=>{
    const listing=row.listing_id?listingById[row.listing_id]:null;
    const vessel=listing?vesselById[listing.vessel_id]:null;
    const inquiry=row.inquiry_id?inquiryById[row.inquiry_id]:null;
    const vesselLabel=vessel?`${vessel.year||''} ${vessel.make||''} ${vessel.model||''}`.replace(/\s+/g,' ').trim():null;
    const buyerLabel=row.buyer_name||inquiry?.lead_name||null;
    const broker=profileById[row.broker_user_id];
    return {...row,
      display_title:row.title||vesselLabel||human(row.event_type||'showing'),
      vessel_label:vesselLabel,
      buyer_label:buyerLabel,
      broker_label:broker?.full_name||broker?.email||null,
      display_location:row.location||vessel?.location||null,
    };
  }),[showings,listingById,vesselById,inquiryById,profileById]);

  const filtered=useMemo(()=>enriched.filter((row)=>{
    if(statusFilter!=='all'&&row.status!==statusFilter)return false;
    if(brokerFilter!=='all'&&row.broker_user_id!==brokerFilter)return false;
    return true;
  }),[enriched,statusFilter,brokerFilter]);

  const byDay=useMemo(()=>{
    const result:Record<string,CalendarEvent[]>={};
    filtered.forEach((row)=>{
      const key=localDateKey(new Date(row.scheduled_at));
      (result[key] ||= []).push(row);
    });
    Object.values(result).forEach((rows)=>rows.sort((a,b)=>new Date(a.scheduled_at).getTime()-new Date(b.scheduled_at).getTime()));
    return result;
  },[filtered]);

  const now=Date.now();
  const upcoming=useMemo(()=>filtered.filter((row)=>new Date(row.scheduled_at).getTime()>=now&&row.status!=='cancelled').sort((a,b)=>new Date(a.scheduled_at).getTime()-new Date(b.scheduled_at).getTime()),[filtered,now]);
  const todayKey=localDateKey(new Date());
  const todayCount=(byDay[todayKey]||[]).filter((row)=>row.status!=='cancelled').length;
  const next7=upcoming.filter((row)=>new Date(row.scheduled_at).getTime()<=now+7*86400000).length;
  const thisMonth=filtered.filter((row)=>isSameMonth(new Date(row.scheduled_at),month)&&row.status!=='cancelled').length;
  const cells=monthCells(month);

  function openNew(date?:Date){
    const base=date?new Date(date):new Date();
    if(date){base.setHours(10,0,0,0)}else{base.setMinutes(Math.ceil(base.getMinutes()/15)*15,0,0)}
    setEditing(null);setSeedDate(toLocalInput(base.toISOString()));setFormError('');setEditorOpen(true);
  }
  function openEdit(row:Row){setEditing(row);setSeedDate('');setFormError('');setEditorOpen(true)}

  async function saveEvent(event:FormEvent<HTMLFormElement>){
    event.preventDefault();if(!supabase||!canEdit)return;
    setSaving(true);setFormError('');
    const form=new FormData(event.currentTarget);
    const get=(key:string)=>String(form.get(key)||'').trim();
    const scheduled=get('scheduled_at');
    if(!scheduled){setFormError('Choose a date and time.');setSaving(false);return}
    const start=new Date(scheduled);if(Number.isNaN(start.getTime())){setFormError('Choose a valid date and time.');setSaving(false);return}
    const duration=Math.max(15,Number(get('duration_minutes')||60));
    const listingId=get('listing_id')||null;
    const listing=listingId?listingById[listingId]:null;
    const vessel=listing?vesselById[listing.vessel_id]:null;
    const defaultTitle=vessel?`${vessel.year||''} ${vessel.make||''} ${vessel.model||''}`.replace(/\s+/g,' ').trim():human(get('event_type')||'showing');
    const brokerId=get('broker_user_id')||listing?.listing_broker_user_id||currentUserId||null;
    const payload={
      organization_id:organization.id,
      listing_id:listingId,
      title:get('title')||defaultTitle,
      event_type:get('event_type')||'showing',
      scheduled_at:start.toISOString(),
      duration_minutes:duration,
      ends_at:addMinutes(start,duration).toISOString(),
      status:get('status')||'scheduled',
      location:get('location')||null,
      buyer_name:get('buyer_name')||null,
      buyer_phone:get('buyer_phone')||null,
      buyer_email:get('buyer_email')||null,
      external_broker_name:get('external_broker_name')||null,
      external_broker_phone:get('external_broker_phone')||null,
      external_broker_email:get('external_broker_email')||null,
      notes:get('notes')||null,
      outcome:get('outcome')||null,
      feedback:get('feedback')||null,
      broker_user_id:brokerId,
      source:editing?.source||'calendar_manual',
    };
    let result;
    if(editing)result=await supabase.from('marine_listing_showings').update(payload).eq('id',editing.id).eq('organization_id',organization.id);
    else result=await supabase.from('marine_listing_showings').insert({...payload,created_by:currentUserId||null});
    if(result.error){setFormError(result.error.message);setSaving(false);return}
    setEditorOpen(false);setEditing(null);await load();setSaving(false);
  }

  async function cancelEvent(){
    if(!supabase||!editing||!canEdit)return;
    setSaving(true);const result=await supabase.from('marine_listing_showings').update({status:'cancelled'}).eq('id',editing.id).eq('organization_id',organization.id);
    if(result.error){setFormError(result.error.message);setSaving(false);return}
    setEditorOpen(false);await load();setSaving(false);
  }

  async function deleteEvent(){
    if(!supabase||!editing||!canEdit)return;
    if(!window.confirm('Delete this calendar entry permanently?'))return;
    setSaving(true);const result=await supabase.from('marine_listing_showings').delete().eq('id',editing.id).eq('organization_id',organization.id);
    if(result.error){setFormError(result.error.message);setSaving(false);return}
    setEditorOpen(false);await load();setSaving(false);
  }

  function exportIcs(){
    const esc=(value:any)=>String(value||'').replace(/\\/g,'\\\\').replace(/\n/g,'\\n').replace(/,/g,'\\,').replace(/;/g,'\\;');
    const stamp=(value:any)=>new Date(value).toISOString().replace(/[-:]/g,'').replace(/\.\d{3}Z$/,'Z');
    const events=filtered.filter((row)=>row.status!=='cancelled');
    const lines=['BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//MarineOps//Showings Calendar//EN','CALSCALE:GREGORIAN'];
    events.forEach((row)=>{
      const start=new Date(row.scheduled_at);const end=row.ends_at?new Date(row.ends_at):addMinutes(start,Number(row.duration_minutes||60));
      lines.push('BEGIN:VEVENT',`UID:${row.id}@aqua-operations.com`,`DTSTAMP:${stamp(new Date())}`,`DTSTART:${stamp(start)}`,`DTEND:${stamp(end)}`,`SUMMARY:${esc(row.display_title)}`,`LOCATION:${esc(row.display_location||'')}`,`DESCRIPTION:${esc([row.buyer_label&&`Client: ${row.buyer_label}`,row.notes].filter(Boolean).join('\n'))}`,'END:VEVENT');
    });
    lines.push('END:VCALENDAR');
    const blob=new Blob([lines.join('\r\n')],{type:'text/calendar;charset=utf-8'});const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=`${organization.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}-showings.ics`;a.click();URL.revokeObjectURL(url);
  }

  const teamOptions=members.filter((row)=>row.active).map((row)=>({id:row.user_id,label:profileById[row.user_id]?.full_name||profileById[row.user_id]?.email||human(row.role)}));

  const nav=navHost?createPortal(<button type="button" className={`nav-button showings-calendar-nav-button ${open?'active':''}`} onClick={()=>setOpen(true)}><span>Showings Calendar</span>{upcoming.length?<b>{upcoming.length}</b>:null}</button>,navHost):null;

  const page=open&&pageHost?createPortal(<div className="showings-calendar-page">
    <section className="showings-calendar-hero">
      <div><p className="eyebrow">Brokerage schedule</p><h2>Showings Calendar</h2><p>Every listing showing feeds this calendar automatically. Add standalone appointments here when the schedule starts outside a listing.</p></div>
      <div className="showings-calendar-actions"><button type="button" className="secondary-button" onClick={exportIcs}>Export .ics</button>{canEdit&&<button type="button" className="primary-button" onClick={()=>openNew()}>+ Add showing</button>}</div>
    </section>
    {error&&<div className="auth-alert error">{error}</div>}
    <section className="showings-stat-grid">
      <div className="showings-stat"><span>Today</span><strong>{todayCount}</strong><small>scheduled appointments</small></div>
      <div className="showings-stat"><span>Next 7 days</span><strong>{next7}</strong><small>upcoming appointments</small></div>
      <div className="showings-stat"><span>{month.toLocaleDateString('en-CA',{month:'short'})}</span><strong>{thisMonth}</strong><small>calendar events</small></div>
      <div className="showings-stat"><span>Listing feed</span><strong>{filtered.filter((row)=>row.listing_id).length}</strong><small>linked to inventory</small></div>
    </section>
    <section className="showings-calendar-layout">
      <div className="panel showings-calendar-panel">
        <div className="showings-toolbar">
          <div className="showings-month-nav"><button type="button" onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()-1,1))}>‹</button><button type="button" className="today-button" onClick={()=>setMonth(new Date(new Date().getFullYear(),new Date().getMonth(),1))}>Today</button><button type="button" onClick={()=>setMonth(new Date(month.getFullYear(),month.getMonth()+1,1))}>›</button><h3>{monthLabel(month)}</h3></div>
          <div className="showings-filters"><select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}><option value="all">All statuses</option><option value="scheduled">Scheduled</option><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="no_show">No show</option></select><select value={brokerFilter} onChange={(e)=>setBrokerFilter(e.target.value)}><option value="all">All brokers</option>{teamOptions.map((row)=><option value={row.id} key={row.id}>{row.label}</option>)}</select></div>
        </div>
        {loading?<div className="showings-loading">Loading showings…</div>:<><div className="calendar-weekdays">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day)=><span key={day}>{day}</span>)}</div><div className="showings-month-grid">{cells.map((day)=>{
          const key=localDateKey(day);const events=byDay[key]||[];const current=isSameMonth(day,month);const today=key===todayKey;
          return <button type="button" key={key} className={`showings-day ${current?'':'outside'} ${today?'today':''}`} onClick={()=>canEdit&&openNew(day)}><span className="showings-day-number">{day.getDate()}</span><div className="showings-day-events">{events.slice(0,3).map((row)=><span role="button" tabIndex={0} key={row.id} className={`showings-event status-${row.status}`} onClick={(event)=>{event.stopPropagation();openEdit(row)}} onKeyDown={(event)=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();event.stopPropagation();openEdit(row)}}}><em>{timeLabel(row.scheduled_at)}</em>{row.display_title}</span>)}{events.length>3&&<small>+{events.length-3} more</small>}</div></button>
        })}</div></>}
      </div>
      <aside className="panel showings-agenda">
        <div className="showings-agenda-heading"><div><p className="eyebrow">Next up</p><h3>Upcoming showings</h3></div>{canEdit&&<button type="button" className="secondary-button compact" onClick={()=>openNew()}>Add</button>}</div>
        {upcoming.length?upcoming.slice(0,12).map((row)=><button type="button" className="agenda-item" key={row.id} onClick={()=>openEdit(row)}><span className={`agenda-date status-${row.status}`}><strong>{new Date(row.scheduled_at).getDate()}</strong><small>{new Date(row.scheduled_at).toLocaleDateString('en-CA',{month:'short'})}</small></span><span className="agenda-copy"><strong>{row.display_title}</strong><small>{timeLabel(row.scheduled_at)}{row.buyer_label?` · ${row.buyer_label}`:''}</small><small>{row.display_location||row.broker_label||human(row.status)}</small></span></button>):<div className="platform-empty">No upcoming showings. Click a calendar day to add one.</div>}
      </aside>
    </section>
    {editorOpen&&<div className="showings-modal-backdrop" onMouseDown={(event)=>{if(event.target===event.currentTarget&&!saving)setEditorOpen(false)}}><div className="showings-modal" role="dialog" aria-modal="true"><div className="showings-modal-heading"><div><p className="eyebrow">{editing?'Calendar appointment':'New appointment'}</p><h3>{editing?'Edit showing':'Add to showings calendar'}</h3></div><button type="button" className="modal-x" onClick={()=>!saving&&setEditorOpen(false)}>×</button></div>
      <form key={editing?.id||seedDate} onSubmit={saveEvent} className="showings-form">
        <div className="showings-form-grid">
          <label>Listing<select name="listing_id" defaultValue={editing?.listing_id||''}><option value="">Standalone / no listing</option>{listings.filter((row)=>!['completed','cancelled'].includes(row.stage)).map((listing)=>{const vessel=vesselById[listing.vessel_id];return <option value={listing.id} key={listing.id}>{vessel?`${vessel.year||''} ${vessel.make} ${vessel.model}`:listing.listing_code}</option>})}</select></label>
          <label>Appointment type<select name="event_type" defaultValue={editing?.event_type||'showing'}><option value="showing">Showing</option><option value="sea_trial">Sea trial</option><option value="survey_inspection">Survey / inspection</option><option value="delivery_handover">Delivery / handover</option><option value="broker_appointment">Broker appointment</option><option value="other">Other</option></select></label>
          <label className="wide">Title<input name="title" defaultValue={editing?.title||''} placeholder="Auto-filled from the listing when left blank"/></label>
          <label>Date & time<input name="scheduled_at" type="datetime-local" required defaultValue={editing?toLocalInput(editing.scheduled_at):seedDate}/></label>
          <label>Duration<select name="duration_minutes" defaultValue={String(editing?.duration_minutes||60)}><option value="30">30 minutes</option><option value="45">45 minutes</option><option value="60">1 hour</option><option value="90">1.5 hours</option><option value="120">2 hours</option><option value="180">3 hours</option></select></label>
          <label>Status<select name="status" defaultValue={editing?.status||'scheduled'}><option value="scheduled">Scheduled</option><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="no_show">No show</option></select></label>
          <label>Broker<select name="broker_user_id" defaultValue={editing?.broker_user_id||currentUserId||''}><option value="">Unassigned</option>{teamOptions.map((row)=><option value={row.id} key={row.id}>{row.label}</option>)}</select></label>
          <label className="wide">Location<input name="location" defaultValue={editing?.location||''} placeholder="Marina, slip, meeting point or address"/></label>
          <label>Client / buyer<input name="buyer_name" defaultValue={editing?.buyer_name||''}/></label>
          <label>Client phone<input name="buyer_phone" defaultValue={editing?.buyer_phone||''}/></label>
          <label className="wide">Client email<input name="buyer_email" type="email" defaultValue={editing?.buyer_email||''}/></label>
          <label>Co-operating broker<input name="external_broker_name" defaultValue={editing?.external_broker_name||''}/></label>
          <label>Broker phone<input name="external_broker_phone" defaultValue={editing?.external_broker_phone||''}/></label>
          <label className="wide">Broker email<input name="external_broker_email" type="email" defaultValue={editing?.external_broker_email||''}/></label>
          <label className="wide">Notes<textarea name="notes" rows={3} defaultValue={editing?.notes||''} placeholder="Access instructions, keys, buyer requests, what to prepare…"/></label>
          <label>Outcome<input name="outcome" defaultValue={editing?.outcome||''} placeholder="Interested, follow-up, offer expected…"/></label>
          <label>Feedback<input name="feedback" defaultValue={editing?.feedback||''}/></label>
        </div>
        {editing&&<div className="showings-source-note">{editing.listing_id?'Linked to a MarineOps listing':'Standalone calendar entry'} · {human(editing.source||'listing')}</div>}
        {formError&&<div className="auth-alert error">{formError}</div>}
        <div className="showings-modal-actions">{editing&&canEdit?<div><button type="button" className="text-danger" onClick={deleteEvent} disabled={saving}>Delete</button>{editing.status!=='cancelled'&&<button type="button" className="secondary-button" onClick={cancelEvent} disabled={saving}>Cancel showing</button>}</div>:<span/>}<div><button type="button" className="secondary-button" onClick={()=>setEditorOpen(false)} disabled={saving}>Close</button>{canEdit&&<button className="primary-button" disabled={saving}>{saving?'Saving…':editing?'Save changes':'Add to calendar'}</button>}</div></div>
      </form>
    </div></div>}
  </div>,pageHost):null;

  return <>{nav}{page}</>;
}
